/**
 * API client for scan operations.
 * All endpoints (except direct blob upload) require auth.
 */

import type { Scan, CreateScanRequest, UploadUrlsResponse, ScanListResponse } from '@app-types/scan'
import { apiUrl } from '../config'
import { getAuthHeaders } from '../stores/authStore'

const API_BASE = apiUrl('/api')

export async function createScan(req: CreateScanRequest): Promise<Scan> {
  const res = await fetch(`${API_BASE}/scans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(req),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const detail = body?.detail || res.statusText
    throw new Error(`Create scan failed: ${res.status} — ${detail}`)
  }
  return res.json()
}

export async function getUploadUrls(scanId: string, frameCount: number): Promise<UploadUrlsResponse> {
  const res = await fetch(`${API_BASE}/scans/${scanId}/upload-urls`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ frame_count: frameCount }),
  })
  if (!res.ok) throw new Error(`Get upload URLs failed: ${res.status}`)
  return res.json()
}

export async function uploadFrame(uploadUrl: string, blob: Blob, maxRetries: number = 5): Promise<void> {
  // Direct to Azure Blob with SAS URL — no auth header needed
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'image/jpeg',
        },
        body: blob,
      })
      if (!res.ok) throw new Error(`Upload frame failed: ${res.status}`)
      return // Success
    } catch (err) {
      if (attempt === maxRetries) throw err
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s (capped at 30s)
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 30000)
      await new Promise(r => setTimeout(r, delay))
    }
  }
}

export async function markScanComplete(scanId: string): Promise<Scan> {
  const res = await fetch(`${API_BASE}/scans/${scanId}/complete`, {
    method: 'POST',
    headers: { ...getAuthHeaders() },
  })
  if (!res.ok) throw new Error(`Mark complete failed: ${res.status}`)
  return res.json()
}

export async function getScan(scanId: string): Promise<Scan> {
  const res = await fetch(`${API_BASE}/scans/${scanId}`, {
    headers: { ...getAuthHeaders() },
  })
  if (!res.ok) throw new Error(`Get scan failed: ${res.status}`)
  return res.json()
}

export async function listScans(shopId?: string): Promise<ScanListResponse> {
  const params = shopId ? `?shopId=${shopId}` : ''
  const res = await fetch(`${API_BASE}/scans${params}`, {
    headers: { ...getAuthHeaders() },
  })
  if (!res.ok) throw new Error(`List scans failed: ${res.status}`)
  return res.json()
}
